<template>
    <div class="ah-page analysis-cash-page">
        <div class="page-header bottom-margin">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>現金流量分析</h1>
                    </div>
                    <div class="actions"></div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div style="margin-bottom: 12px">
                <el-select v-model="year" placeholder="年度" @change="updateData">
                    <el-option v-for="item in yearList" :key="item" :label="item" :value="item" />
                </el-select>
                <span style="font-size: 11pt; margin: 0 0 0 10px; color: #666">年度現金流量明細 </span>
            </div>
            <el-row :gutter="20">
                <el-col :sm="12">
                    <div class="ah-panel">
                        <div class="panel-header gray small">
                            <div class="panel-title">年度單月金流變化</div>
                        </div>
                        <div class="panel-body">
                            <v-chart
                                :option="monthlyCashChart"
                                style="width: 100%; height: 200px"
                                v-loading="isLoading"
                                element-loading-background="rgba(255, 255, 255, 0.5)"
                                element-loading-text="載入中"
                            />
                        </div>
                    </div>
                </el-col>
                <el-col :sm="12">
                    <div class="ah-panel">
                        <div class="panel-header gray small">
                            <div class="panel-title">年度累計金流變化</div>
                        </div>
                        <div class="panel-body">
                            <v-chart
                                :option="monthlyAccumulateCashChart"
                                style="width: 100%; height: 200px"
                                v-loading="isLoading"
                                element-loading-background="rgba(255, 255, 255, 0.5)"
                                element-loading-text="載入中"
                            />
                        </div>
                    </div>
                </el-col>
            </el-row>
            <div class="ah-panel">
                <div class="panel-body">
                    <div class="ah-statistics-bar summary-cash-panel" style="margin-top: 15px; border-bottom: 0;">
                        <div class="item">
                            <div class="inner">
                                <div class="label">當月營業實收累計</div>
                                <div class="value">
                                    <Money :v="summaryData.monthlyIncomeTotal" :showPositivePlus="true" :class="trendColorClass(true, summaryData.monthlyIncomeTotal)" />
                                </div>
                            </div>
                        </div>
                        <div class="item">
                            <div class="inner">
                                <div class="label">當月營業實付累計</div>
                                <div class="value">
                                    <Money :v="summaryData.monthlyPayoutTotal" :showPositivePlus="true" :class="trendColorClass(true, summaryData.monthlyPayoutTotal)" />
                                </div>
                            </div>
                        </div>
                        <div class="item spacer" />
                        <div class="item">
                            <div class="inner">
                                <div class="label">年度營業現金變化</div>
                                <div class="value">
                                    <Money :v="summaryData.annualActualSub" :class="trendColorClass(true, summaryData.annualActualSub)" show-positive-plus />
                                </div>
                            </div>
                        </div>
                        <div class="item">
                            <div class="inner">
                                <div class="label">年度財務收支變化</div>
                                <div class="value">
                                    <Money :v="summaryData.annualSub" :class="trendColorClass(true, summaryData.annualSub)" show-positive-plus />
                                </div>
                            </div>
                        </div>
                        <div class="item">
                            <div class="inner">
                                <div class="label">年度金流變化</div>
                                <div class="value">
                                    <Money :v="summaryData.annualFlowEvolution" :class="trendColorClass(true, summaryData.annualFlowEvolution)" show-positive-plus />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style="color: #888; font-size: 10pt; margin: 15px 0;">*依來源單據類型作為統計基準。</div>
                    <el-table
                        :data="monthlyCashTable"
                        row-key="id"
                        border
                        class="ah-analysis-table monthly-cash-table"
                        :tree-props="{ children: 'children' }"
                        v-loading="isLoading"
                        empty-text="無資料"
                    >
                        <el-table-column prop="name" label="項目" fixed width="220">
                            <template slot-scope="scope">
                                <span>
                                    <span>
                                        <span>{{ scope.row.name }}</span>
                                        <ah-tip v-if="scope.row.tip != ''" :secret-text="scope.row.secret">
                                            <div>{{ scope.row.tip }}</div>
                                        </ah-tip>
                                    </span>
                                    <div style="color: #aaa; margin-left: 35px">
                                        {{ scope.row.subtitle }}
                                    </div>
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column v-for="(month, index) in 12" :key="`month${month}`" :prop="`m${index}`" :label="getZhMonthByIndex(index)" align="right">
                            <template slot-scope="scope">
                                <Money
                                    :v="scope.row[`m${index}`]"
                                    zero="-"
                                    :showPositivePlus="scope.row.trendColor"
                                    :class="trendColorClass(scope.row.trendColor || scope.row[`m${index}`] < 0, scope.row[`m${index}`])"
                                    round
                                />
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-alert :closable="false" style="line-height: 150%; margin-top: 15px">
                        <ul>
                            <li>
                                分析來源日期：請款單實際付款日、發票入帳日期。
                            </li>
                            <li>外幣餘額以"即時匯率"換算約當台幣。</li>
                        </ul>
                    </el-alert>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import _ from 'lodash';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import {
    TitleComponent, TooltipComponent, LegendComponent, GridComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';

use([CanvasRenderer, PieChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent]);

const monthZh = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

export default {
    name: 'AnalysisCash',
    components: { VChart },
    data() {
        return {
            isLoading: true,

            year: new Date().getFullYear(),
            yearList: [],
            monthlyCashTable: [],
            summaryData: {
                monthlyIncomeTotal: 0,
                monthlyPayoutTotal: 0,
                annualActualSub: 0,
                annualSub: 0,
                annualFlowEvolution: 0,
            },

            // 每月現金流量圖
            monthlyCashChart: {
                grid: {
                    left: 30,
                    top: 60,
                    right: 50,
                    bottom: 30,
                    containLabel: true,
                },
                legend: { data: [] },
                tooltip: { trigger: 'axis' },
                color: ['#91cc75', '#5470c6', '#fac858', '#ee6666'],
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: monthZh,
                },
                yAxis: { type: 'value' },
                series: [],
            },
            monthlyAccumulateCashChart: {
                grid: {
                    left: 30,
                    top: 60,
                    right: 50,
                    bottom: 30,
                    containLabel: true,
                },
                legend: { data: [] },
                tooltip: { trigger: 'axis' },
                color: ['#91cc75', '#5470c6', '#fac858', '#ee6666'],
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: monthZh,
                },
                yAxis: { type: 'value' },
                series: [],
            },
        };
    },
    created() {
        // 取得年份列表
        this.fetchYearOptions();
    },
    methods: {
        // 數字轉中文數字
        getZhMonthByIndex(index) {
            return monthZh[index];
        },

        // 取得年份列表
        async fetchYearOptions() {
            const resp = await this.$api({
                url: 'FinancialAnalysisPage/GetPaymentFlowAnalysisYearOptions',
                method: 'GET',
            });

            this.status = 'success';

            // 年份多的在前
            resp.data.options.sort((a, b) => b - a);

            // 年份列表
            this.yearList = resp.data.options?.length > 0 ? resp.data.options : [new Date().getFullYear()];

            this.year = _.head(this.yearList);
            this.updateData();
        },

        // 更新統計資料
        async updateData() {
            this.isLoading = true;

            const result = await this.$api({
                url: `FinancialAnalysisPage/GetPaymentFlowAnalysis?Year=${this.year}`,
                method: 'GET',
            });

            const { monthlyPaymentFlow } = result.data;
            // 圖表
            this.updateChart(monthlyPaymentFlow);

            // 表格
            this.updateMonthlyCashTable(monthlyPaymentFlow);

            // 年度現金流量明細
            this.updateSummaryData(monthlyPaymentFlow);

            this.isLoading = false;
        },

        // 圖表
        updateChart(monthlyPaymentFlow) {
            const series = [
                {
                    name: '營業現金變化',
                    data: [],
                    type: 'line',
                },
                {
                    name: '財務收支變化',
                    data: [],
                    type: 'line',
                },
                {
                    name: '當月金流變化',
                    data: [],
                    type: 'line',
                },
            ];
            const accSeries = [
                {
                    name: '營業現金變化累計',
                    data: [],
                    type: 'line',
                },
                {
                    name: '財務收支變化累計',
                    data: [],
                    type: 'line',
                },
                {
                    name: '當月金流變化累計',
                    data: [],
                    type: 'line',
                },
            ];

            const actualSub = _.find(series, ['name', '營業現金變化']);
            const monthlySub = _.find(series, ['name', '財務收支變化']);
            const flowEvolution = _.find(series, ['name', '當月金流變化']);

            const accActualSub = _.find(accSeries, ['name', '營業現金變化累計']);
            const accMonthlySub = _.find(accSeries, ['name', '財務收支變化累計']);
            const accFlowEvolution = _.find(accSeries, ['name', '當月金流變化累計']);

            monthlyPaymentFlow.forEach((o) => {
                actualSub.data.push(Math.round(o.inc - o.pao));
                monthlySub.data.push(Math.round(o.vaR_INC - o.vaR_PAO));
                flowEvolution.data.push(Math.round(o.baL_CHANGE));

                accActualSub.data.push(_.sum(actualSub.data));
                accMonthlySub.data.push(_.sum(monthlySub.data));
                accFlowEvolution.data.push(_.sum(flowEvolution.data));
            });

            series.forEach((item) => {
                this.monthlyCashChart.legend.data.push(item.name);
            });
            accSeries.forEach((item) => {
                this.monthlyAccumulateCashChart.legend.data.push(item.name);
            });

            // 去除今年未到月份
            if (this.year === new Date().getFullYear()) {
                const currMonth = new Date().getMonth();
                series.forEach(({ data }, s) => {
                    data.forEach((item, i) => {
                        if (i > currMonth && item === 0) {
                            series[s].data[i] = null;
                            accSeries[s].data[i] = null;
                        }
                    });
                });
            }

            this.monthlyCashChart.series = series;
            this.monthlyAccumulateCashChart.series = accSeries;
        },

        // 每月現金表格
        updateMonthlyCashTable(monthlyPaymentFlow) {
            const data = [
                {
                    id: 'initial-sum',
                    name: '期初總計',
                    tip: '期初台幣存款 + 期初外幣存款 + 期初零用現金',
                    children: [
                        {
                            id: 'initial-twd',
                            name: '期初台幣存款',
                            tip: '',
                            dataFieldName: 'deF_TWD_BAL',
                        },
                        {
                            id: 'initial-cny',
                            name: '期初外幣存款',
                            subtitle: '(約當台幣)',
                            tip: '',
                            dataFieldName: 'deF_FOREIGN_BAL',
                        },
                        {
                            id: 'initial-petty-cash',
                            name: '期初零用現金',
                            tip: '',
                            dataFieldName: 'deF_CASH_BAL',
                        },
                    ],
                },

                {
                    id: 'monthly-actual-income',
                    name: '當月發票實收',
                    tip: '當月入帳發票總額',
                    secret: '多次入帳sum*rat - 多次退款sum*rat',
                },
                {
                    id: 'monthly-actual-payout',
                    name: '當月請款、薪資實付',
                    tip: '當月已付請款總額 + 當月實付薪資(不含代扣金額)',
                    secret: '請款單sum - 請款單財務支出 +薪資',
                },
                {
                    id: 'monthly-actual-sub',
                    name: '當月營業現金變化',
                    tip: '',
                    trendColor: true,
                },
                {
                    id: 'monthly-income',
                    name: '當月財務收入',
                    tip: '含其它、資產負債類',
                },
                {
                    id: 'monthly-payout',
                    name: '當月財務支出',
                    tip: '含其它、資產負債類',
                },
                {
                    id: 'monthly-sub',
                    name: '當月財務收支變化',
                    tip: '',
                    trendColor: true,
                },
                {
                    id: 'monthly-balance',
                    name: '存借款餘額合併總計',
                    tip: '當月台幣存款 + 當月外幣存款 + 當月台幣借款 + 當月外幣借款 + 當月零用金餘額',
                    children: [
                        {
                            id: 'monthly-balance-twd',
                            name: '當月台幣存款餘額',
                            tip: '期初台幣存款 + 當月台幣營業實收 - 當月台幣營業實付 + 當月台幣財務收入 - 當月台幣財務支出 - 零用現金請領',
                            dataFieldName: 'twD_BAL',
                        },
                        {
                            id: 'monthly-borrow-balance-twd',
                            name: '當月台幣 "借款" 餘額',
                            tip: '銀行帳戶中勾選「借款戶」的總額',
                            dataFieldName: 'twD_Borrow_BAL',
                        },
                        {
                            id: 'monthly-balance-cny',
                            name: '當月外幣存款餘額',
                            subtitle: '(約當台幣)',
                            tip: '(期初外幣存款 + 當月外幣營業實收 - 當月外幣營業實付 + 當月外幣財務收入 - 當月外幣財務支出) * 當期匯率',
                            dataFieldName: 'foreigN_BAL',
                        },
                        {
                            id: 'monthly-borrow-balance-cny',
                            name: '當月外幣 "借款" 餘額',
                            subtitle: '(約當台幣)',
                            tip: '銀行帳戶中勾選「借款戶」的總額',
                            dataFieldName: 'foreigN_Borrow_BAL',
                        },
                        {
                            id: 'monthly-balance-petty-cash',
                            name: '當月零用現金餘額',
                            tip: '期初零用現金 + 領用現金請領 - 零用金結存 - 當月現金營業實付',
                            dataFieldName: 'casH_BAL',
                        },
                    ],
                },
                {
                    id: 'cash-flow-evolution',
                    name: '當月金流變化',
                    // tip: '(當月台幣存款餘額 + 當月外幣存款餘額 + 當月零用現金餘額) - (期初台幣存款 + 期初外幣存款 + 期初零用現金)'
                    // tip: '期初總額 - 當月餘額總額',
                    tip: '期初(台外幣)存款總額 - 當月(台外幣)存款總額',
                    trendColor: true,
                },
            ];

            const initialSum = _.find(data, ['name', '期初總計']);
            const monthlyActualIncome = _.find(data, ['name', '當月發票實收']);
            const monthlyActualPayout = _.find(data, ['name', '當月請款、薪資實付']);
            const monthlyActualSub = _.find(data, ['name', '當月營業現金變化']);
            const monthlyIncome = _.find(data, ['name', '當月財務收入']);
            const monthlyPayout = _.find(data, ['name', '當月財務支出']);
            const monthlySub = _.find(data, ['name', '當月財務收支變化']);
            const monthlyBalance = _.find(data, ['name', '存借款餘額合併總計']);
            const monthlyFlowEvolution = _.find(data, ['name', '當月金流變化']);

            monthlyPaymentFlow.forEach((o, i) => {
                initialSum[`m${i}`] = Math.round(o.deF_TWD_BAL + o.deF_FOREIGN_BAL + o.deF_CASH_BAL);
                initialSum.children.forEach((child, childIndex) => {
                    initialSum.children[childIndex][`m${i}`] = Math.round(o[child.dataFieldName]);
                });

                monthlyActualIncome[`m${i}`] = Math.round(o.inc);
                monthlyActualPayout[`m${i}`] = Math.round(o.pao);
                monthlyActualSub[`m${i}`] = Math.round(o.inc - o.pao);
                monthlyIncome[`m${i}`] = Math.round(o.vaR_INC);
                monthlyPayout[`m${i}`] = Math.round(o.vaR_PAO);
                monthlySub[`m${i}`] = Math.round(o.vaR_INC - o.vaR_PAO);

                monthlyBalance[`m${i}`] = Math.round(
                    o.twD_BAL + o.foreigN_BAL + o.casH_BAL + (o.twD_Borrow_BAL + o.foreigN_Borrow_BAL), // 借款戶
                );
                monthlyBalance.children.forEach((child, childIndex) => {
                    monthlyBalance.children[childIndex][`m${i}`] = Math.round(o[child.dataFieldName]);
                });

                monthlyFlowEvolution[`m${i}`] = Math.round(o.baL_CHANGE);
            });

            this.monthlyCashTable = data;
        },

        updateSummaryData(monthlyPaymentFlow) {
            this.summaryData.monthlyIncomeTotal = 0;
            this.summaryData.monthlyPayoutTotal = 0;

            this.summaryData.annualActualSub = 0;
            this.summaryData.annualSub = 0;
            this.summaryData.annualFlowEvolution = 0;

            monthlyPaymentFlow.forEach((o) => {
                this.summaryData.monthlyIncomeTotal += Math.round(o.inc);
                this.summaryData.monthlyPayoutTotal += Math.round(o.pao);

                this.summaryData.annualActualSub += Math.round(o.inc - o.pao);
                this.summaryData.annualSub += Math.round(o.vaR_INC - o.vaR_PAO);
                this.summaryData.annualFlowEvolution += Math.round(o.baL_CHANGE);
            });
        },

        trendColorClass(show, amount) {
            if (!show || !amount) return '';
            if (amount > 0) return 'increase';
            return 'decrease';
        },
    },
};
</script>
<style lang="scss">
.analysis-cash-page {
    .monthly-cash-table,
    .summary-cash-panel {
        .money {
            display: block;
            white-space: nowrap;
            word-break: keep-all;
            text-overflow: unset;

            &.increase {
                color: green;
            }

            &.decrease {
                color: red;
            }
        }
    }
}
</style>
